import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  MapPin, Search, RefreshCw, Copy, Send, Calendar, CheckCircle, 
  Clock, AlertTriangle, Mail, ExternalLink, Loader2, Truck, Eye
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTrackingLinks = () => {
  const { getAuthHeader } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sendingEmail, setSendingEmail] = useState({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [customEmail, setCustomEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const fetchJobs = async () => {
    setLoading(true);
    try {
      // Fetch all jobs that have tracking capability (in_progress, completed, approved)
      const response = await axios.get(`${API}/admin/jobs`, getAuthHeader());
      // Filter to jobs that can have tracking links
      const trackableJobs = response.data.filter(job => 
        ['open', 'in_progress', 'completed', 'approved', 'paid'].includes(job.status)
      );
      setJobs(trackableJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700"><Truck size={12} className="mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" /> Completed</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" /> Approved</Badge>;
      case 'paid':
        return <Badge className="bg-purple-100 text-purple-700"><CheckCircle size={12} className="mr-1" /> Paid</Badge>;
      case 'open':
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" /> Open</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertTriangle size={12} className="mr-1" /> {status}</Badge>;
    }
  };

  const getTrackingUrl = (jobId) => {
    return `${window.location.origin}/track/${jobId}`;
  };

  const handleCopyLink = (jobId) => {
    const url = getTrackingUrl(jobId);
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied to clipboard!');
  };

  const handleOpenEmailDialog = (job) => {
    setSelectedJob(job);
    setCustomEmail(job.client_email || '');
    setCustomMessage(`Dear ${job.client || 'Customer'},\n\nYou can track the delivery progress for "${job.title}" using the link below.\n\nThank you for choosing Local Flyer Delivery!`);
    setEmailDialogOpen(true);
  };

  const handleSendTrackingEmail = async () => {
    if (!selectedJob) return;
    
    if (!customEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingEmail(prev => ({ ...prev, [selectedJob.id]: true }));

    try {
      const response = await axios.post(
        `${API}/admin/jobs/${selectedJob.id}/send-tracking-email`,
        {
          email: customEmail,
          message: customMessage
        },
        getAuthHeader()
      );
      toast.success(response.data.message || `Tracking email sent to ${customEmail}`);
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [selectedJob.id]: false }));
    }
  };

  const handleViewTracking = (jobId) => {
    window.open(getTrackingUrl(jobId), '_blank');
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group jobs by status
  const groupedJobs = {
    in_progress: filteredJobs.filter(j => j.status === 'in_progress'),
    completed: filteredJobs.filter(j => ['completed', 'approved', 'paid'].includes(j.status)),
    open: filteredJobs.filter(j => j.status === 'open')
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tracking-links-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="text-orange-500" />
            Customer Tracking Links
          </h1>
          <p className="text-gray-500 mt-1">Send delivery tracking links to customers</p>
        </div>
        <Button onClick={fetchJobs} variant="outline" className="flex items-center gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search jobs by title, client, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="tracking-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="tracking-status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="approved">Approved</option>
            <option value="open">Open</option>
          </select>
        </div>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-700">{groupedJobs.in_progress.length}</p>
            </div>
            <Truck className="text-blue-500" size={24} />
          </div>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Completed</p>
              <p className="text-2xl font-bold text-green-700">{groupedJobs.completed.length}</p>
            </div>
            <CheckCircle className="text-green-500" size={24} />
          </div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600">Open Jobs</p>
              <p className="text-2xl font-bold text-amber-700">{groupedJobs.open.length}</p>
            </div>
            <Clock className="text-amber-500" size={24} />
          </div>
        </Card>
      </div>

      {/* Jobs List */}
      <Card className="divide-y">
        {filteredJobs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No jobs found matching your criteria</p>
          </div>
        ) : (
          filteredJobs.map((job) => (
            <div key={job.id} className="p-4 hover:bg-gray-50" data-testid={`tracking-job-${job.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-orange-600" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{job.title}</h3>
                      <p className="text-sm text-gray-500">{job.client}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {getStatusBadge(job.status)}
                        <span className="text-xs text-gray-400">
                          {job.deliveries?.toLocaleString()} deliveries
                        </span>
                        {job.location && (
                          <span className="text-xs text-gray-400">• {job.location}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tracking Link Info */}
                <div className="flex-shrink-0 lg:text-right">
                  <p className="text-xs text-gray-400 mb-1">Tracking Link:</p>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 break-all">
                    /track/{job.id.substring(0, 8)}...
                  </code>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewTracking(job.id)}
                    className="flex items-center gap-1"
                    data-testid={`view-tracking-${job.id}`}
                  >
                    <Eye size={14} />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyLink(job.id)}
                    className="flex items-center gap-1"
                    data-testid={`copy-tracking-${job.id}`}
                  >
                    <Copy size={14} />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleOpenEmailDialog(job)}
                    disabled={sendingEmail[job.id]}
                    className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid={`send-tracking-email-${job.id}`}
                  >
                    {sendingEmail[job.id] ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Mail size={14} />
                    )}
                    Send Email
                  </Button>
                </div>
              </div>

              {/* Client Email Info */}
              {job.client_email && (
                <div className="mt-2 ml-13 pl-13">
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Mail size={12} />
                    Client Email: {job.client_email}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="text-orange-500" size={20} />
              Send Tracking Link Email
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-900">{selectedJob.title}</p>
                <p className="text-sm text-gray-500">{selectedJob.client}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <Input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="tracking-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message (Optional)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Add a custom message..."
                  data-testid="tracking-message-input"
                />
              </div>
              
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-sm font-medium text-orange-800">Tracking Link:</p>
                <code className="text-xs text-orange-600 break-all">
                  {getTrackingUrl(selectedJob.id)}
                </code>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTrackingEmail}
              disabled={sendingEmail[selectedJob?.id]}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="confirm-send-tracking-email"
            >
              {sendingEmail[selectedJob?.id] ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrackingLinks;
